/* PlannerInbox — iter101.21
 *
 * Unified "act on this NOW" feed for the planner dashboard. Mirror of
 * the doctor-portal NeedsMeNow component but scoped to the signals a
 * planner actually cares about:
 *   • doctor_responded    — doctor supplied the requested info
 *   • pending_acceptance  — new case routed by admin, not yet accepted
 *   • plan_revision       — doctor asked for changes on the submitted plan
 *   • refinement          — doctor requested a refinement round
 *   • doctor_chat         — chat reply from doctor on a case-thread the
 *                           planner has not read yet (case_messages)
 *
 * Fetches from `GET /api/planner/needs-me-now`. Polls every 60s so a
 * doctor reply lands in the planner's face within a minute without
 * requiring a manual refresh (SSE parity later if planner headcount
 * warrants).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    ChatCircleDots, ClipboardText, ArrowsClockwise, PencilSimple,
    ChatTeardropDots, Tray, ArrowRight, Bell, Package,
} from "@phosphor-icons/react";


const KIND_META = {
    doctor_responded:   { Icon: ChatCircleDots,    ring: "bg-emerald-50 border-emerald-200 text-emerald-700",  badge: "bg-emerald-600 text-white",  label: "Doctor responded" },
    pending_acceptance: { Icon: ClipboardText,     ring: "bg-amber-50 border-amber-200 text-amber-700",         badge: "bg-amber-600 text-white",    label: "Pending acceptance" },
    plan_revision:      { Icon: ArrowsClockwise,   ring: "bg-amber-50 border-amber-200 text-amber-700",         badge: "bg-amber-600 text-white",    label: "Plan revision" },
    refinement:         { Icon: PencilSimple,      ring: "bg-violet-50 border-violet-200 text-violet-700",      badge: "bg-violet-600 text-white",   label: "Refinement" },
    /* iter101.91 — After a doctor approves the plan, the case sits in
       `approved_pending_mfg` until the planner finalises the MFG spec.
       Rose tone matches the dashboard tile of the same name. */
    mfg_spec_pending:   { Icon: Package,           ring: "bg-rose-50 border-rose-200 text-rose-700",            badge: "bg-rose-600 text-white",     label: "MFG spec pending" },
    doctor_chat:        { Icon: ChatTeardropDots,  ring: "bg-sky-50 border-sky-200 text-sky-700",               badge: "bg-sky-600 text-white",      label: "Doctor chat" },
};


export default function PlannerInbox() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const pollRef = useRef(null);

    const load = () => {
        api.get("/planner/needs-me-now")
            .then((r) => setData(r.data || { items: [], counts: {} }))
            .catch(() => setData({ items: [], counts: {} }))
            .finally(() => { setLoading(false); setFirstLoad(false); });
    };

    useEffect(() => {
        load();
        // Poll every 60s. Refresh runs are cheap on the server (single
        // Mongo query over the planner's own cases) so 1 min cadence is
        // plenty for the SLA windows we care about.
        pollRef.current = setInterval(load, 60_000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const items = data?.items || [];
    const counts = data?.counts || {};

    // Empty inbox = the card collapses to a small "clear" line. No one
    // wants a giant "Nothing here ✅" banner glued to the top.
    if (!firstLoad && !loading && items.length === 0) {
        return (
            <div
                className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 flex items-center gap-3"
                data-testid="planner-inbox-empty"
            >
                <Tray size={16} className="text-emerald-700 shrink-0" weight="fill" />
                <div className="text-sm text-emerald-900 font-semibold">Inbox zero</div>
                <div className="text-xs text-emerald-800/80">No doctor responses, chats, or handoffs waiting on you right now.</div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="planner-inbox">
            <div className="flex items-center justify-between px-4 py-3 bg-secondary/40 border-b border-border">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary grid place-items-center">
                        <Bell size={14} weight="fill" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-foreground">Your Inbox</div>
                        <div className="text-[11px] text-muted-foreground">
                            {loading && firstLoad
                                ? "Loading…"
                                : items.length === 1
                                    ? "1 case needs your action"
                                    : `${items.length} cases need your action`}
                        </div>
                    </div>
                </div>
                {/* Kind counts strip — small colored pills showing the mix */}
                <div className="hidden md:flex items-center gap-1.5">
                    {["doctor_responded", "pending_acceptance", "plan_revision", "refinement", "mfg_spec_pending", "doctor_chat"].map((k) => {
                        const n = counts[k] || 0;
                        if (!n) return null;
                        const m = KIND_META[k];
                        return (
                            <span
                                key={k}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${m.ring}`}
                                data-testid={`planner-inbox-count-${k}`}
                            >
                                <m.Icon size={9} weight="fill" /> {n}
                            </span>
                        );
                    })}
                </div>
            </div>
            <div className="divide-y divide-border/60 max-h-[420px] overflow-y-auto">
                {loading && firstLoad ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">Loading your inbox…</div>
                ) : (
                    items.map((it) => <InboxRow key={`${it.kind}:${it.case_id}`} item={it} />)
                )}
            </div>
        </div>
    );
}


function InboxRow({ item }) {
    const m = KIND_META[item.kind] || KIND_META.doctor_chat;
    return (
        <Link
            to={item.href}
            className="flex items-start gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors group"
            data-testid={`planner-inbox-row-${item.kind}-${item.case_id}`}
        >
            <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 border ${m.ring}`}>
                <m.Icon size={16} weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${m.badge}`}
                    >
                        {m.label}
                    </span>
                    {item.code && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                            {item.code}
                        </span>
                    )}
                    {item.rel && (
                        <span className="text-[10px] text-muted-foreground ml-auto">{item.rel}</span>
                    )}
                </div>
                <div className="text-[13px] text-foreground font-medium mt-1 line-clamp-1">
                    {item.title}
                </div>
                {item.sub && (
                    <div className="text-[11.5px] text-muted-foreground mt-0.5 line-clamp-2">
                        {item.sub}
                    </div>
                )}
            </div>
            <ArrowRight
                size={13}
                className="text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all mt-1 shrink-0"
                weight="bold"
            />
        </Link>
    );
}
